import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as authService from "@/services/auth";
import {
  AUTH_TOKEN_KEY,
  USER_DATA_KEY,
  clearAuthSession,
  dispatchStorageEvent,
  normalizeTradingUserStatus,
  isPendingApprovalStatus,
} from "@/utils/auth";

const AuthContext = createContext(null);

function persistSession(userData) {
  const jwt = userData?.token || userData?.verificationToken;
  const normalized = {
    ...userData,
    status: normalizeTradingUserStatus(userData?.status) ?? userData?.status,
  };

  if (jwt) {
    localStorage.setItem(AUTH_TOKEN_KEY, jwt);
    dispatchStorageEvent(AUTH_TOKEN_KEY, jwt);
  }
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(normalized));
  return normalized;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // بررسی session ذخیره شده برای همه مسیرها، از جمله login
    // تا کاربر لاگین‌شده با باز کردن /login دوباره وارد برنامه شود.
    const storedUser = localStorage.getItem(USER_DATA_KEY);
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);

    if (storedUser && storedToken) {
      try {
        const userData = JSON.parse(storedUser);
        setUser({
          ...userData,
          status: normalizeTradingUserStatus(userData?.status) ?? userData?.status,
        });
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem(USER_DATA_KEY);
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const syncAuthState = (nextToken) => {
      if (!nextToken) {
        setUser(null);
        return;
      }

      const storedUser = localStorage.getItem(USER_DATA_KEY);
      if (!storedUser) return;

      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing stored user data during sync:", error);
        clearAuthSession();
        setUser(null);
      }
    };

    const handleStorageChange = (event) => {
      if (event.key === AUTH_TOKEN_KEY) {
        syncAuthState(event.newValue);
      }
    };

    const handleCustomStorageChange = (event) => {
      if (event.detail?.key === AUTH_TOKEN_KEY) {
        syncAuthState(event.detail.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, []);

  /**
   * Login with username and password
   */
  const login = async ({ username, password }) => {
    const userData = await authService.login({ username, password });
    const normalized = persistSession(userData);
    setUser(normalized);
    return normalized;
  };

  /**
   * Register trader (no OTP / no JWT). User must wait for admin confirmation.
   * Does not store token or set user — registration returns no JWT.
   */
  const mobileRegister = ({ fullName, phoneNumber, provinceId, cityId, address }) =>
    authService.mobileRegister({ fullName, phoneNumber, provinceId, cityId, address });

  /**
   * Login with mobile number (sends OTP) — does not store JWT
   */
  const mobileLogin = ({ phoneNumber }) => authService.mobileLogin({ phoneNumber });

  /**
   * Verify login with token and OTP code — stores JWT
   */
  const verifyLogin = async ({ token, code }) => {
    const userData = await authService.verifyLogin({ token, code });
    const normalized = persistSession(userData);
    setUser(normalized);
    return normalized;
  };

  /**
   * Verify register with token and code (OTP verification for registration)
   * Kept for compatibility; not used by current UI.
   */
  const verifyRegister = async ({ token, code }) => {
    const response = await authService.verifyRegister({ token, code });
    const normalized = persistSession(response);
    setUser(normalized);
    return normalized;
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearAuthSession();
      setUser(null);
    }
  };

  const value = useMemo(
    () => ({
      isAuthenticated: !!user,
      user,
      loading,
      login,
      mobileRegister,
      mobileLogin,
      verifyLogin,
      verifyRegister,
      logout,
      isPendingApproval: isPendingApprovalStatus(user?.status),
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
